const API_BASE_URL = '/api';

export interface LoginCredentials {
  username: string;
  password: string;
}

export interface RegisterData {
  username: string;
  email: string;
  password: string;
}

interface AuthResponse {
  token: string;
  userId: number;
  username: string;
  is_admin?: boolean;
  is_read_only?: boolean;
  message?: string;
}

interface MessageResponse {
  message: string;
}

// Token expiration handler - can be set by AuthContext
let onTokenExpired: (() => void) | null = null;

export const setTokenExpirationHandler = (handler: () => void) => {
  onTokenExpired = handler;
};

// Helper function to handle API requests
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  // Use a plain object for headers to allow custom properties like Authorization
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (options.headers) {
    // Merge existing headers from options
    Object.entries(options.headers).forEach(([key, value]) => {
      if (typeof value === 'string') {
        requestHeaders[key] = value;
      }
    });
  }

  const token = localStorage.getItem('authToken');
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, { 
    ...options, 
    headers: requestHeaders 
  });

  // Handle token expiration
  if (response.status === 401) {
    const errorData = await response.json().catch(() => ({ message: 'Unauthorized' }));
    
    // Check if it's a token expiration issue
    if (errorData.message?.toLowerCase().includes('expired') || 
        errorData.message?.toLowerCase().includes('invalid') ||
        errorData.message?.toLowerCase().includes('unauthorized')) {
      console.warn('Token expired or invalid, triggering logout...');
      
      // Clear local storage immediately
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      
      // Call the logout handler if available
      if (onTokenExpired) {
        onTokenExpired();
      }
      
      throw new Error('Your session has expired. Please log in again.');
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

// Registration - uses fetch directly to avoid token expiration interceptor
export const register = async (userData: RegisterData): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Registration failed' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Login - uses fetch directly to avoid the token expiration interceptor
// which misinterprets "Invalid credentials" (401) as a session expiry
export const login = async (credentials: LoginCredentials): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Login failed' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return response.json();
};

// Forgot Password
export const forgotPassword = async (emailData: { email: string }): Promise<MessageResponse> => {
  return fetchApi<MessageResponse>(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    body: JSON.stringify(emailData),
  });
};

// Reset Password
export const resetPassword = async (token: string, passwordData: { password: string }): Promise<MessageResponse> => {
  return fetchApi<MessageResponse>(`${API_BASE_URL}/auth/reset-password/${token}`, {
    method: 'POST',
    body: JSON.stringify(passwordData),
  });
};

// Example of a protected route call (we'll need a backend route for this)
// export const getMyProfile = async (): Promise<any> => {
//   return fetchApi<any>(`${API_BASE_URL}/users/profile`); // Assuming you create a /api/users/profile route protected by authMiddleware
// };

// Add other auth-related API calls here as needed (e.g., logout, verify-token) 
